export const DOSSIER_LESSONS = [
  {
    id: 'dossier-1',
    numero: 1,
    titre: "Identifier les pièces nécessaires",
    sousTitre: "Quoi rassembler ?",
    contexte: "Vous devez constituer un dossier pour la CAF, la CPAM ou une demande de logement. Par où commencer ?",
    explication: [
      "**Lisez la liste complète** avant de commencer à rassembler quoi que ce soit. Rien de plus frustrant que de s'arrêter à mi-chemin parce qu'il manque un document.",
      "**Pièces quasi-universelles** : pièce d'identité, justificatif de domicile -3 mois, RIB, et selon le cas : avis d'imposition, bulletins de salaire.",
      "**Dates de validité** : carte d'identité (10 ans), passeport (10 ans), justificatif de domicile (3 mois maximum). Vérifiez avant de tout rassembler.",
      "**Si un document manque** : ne bloquez pas votre dossier. Signalez le document manquant à l'organisme et envoyez le reste — ils accordent souvent un délai.",
      "**Créez une liste** sur papier ou téléphone et cochez au fur et à mesure. Ça évite les oublis et les allers-retours.",
    ],
    question: "Votre justificatif de domicile date de 4 mois. Que faire ?",
    options: [
      { id: 'a', text: "En trouver un plus récent — facture EDF ou quittance de loyer du mois dernier", correct: true },
      { id: 'b', text: "L'envoyer quand même, 4 mois c'est proche de 3 mois", correct: false },
      { id: 'c', text: "Expliquer par écrit que vous n'en avez pas de plus récent", correct: false },
    ],
    explication_reponse: "Un justificatif de plus de 3 mois sera systématiquement refusé — c'est une règle stricte. Cherchez une facture récente : EDF, eau, internet, téléphone fixe, ou votre dernière quittance de loyer.",
  },
  {
    id: 'dossier-2',
    numero: 2,
    titre: "Scanner ou photographier un document",
    sousTitre: "La bonne qualité",
    contexte: "Vous devez envoyer des documents en ligne. Comment les numériser correctement ?",
    explication: [
      "**Avec un scanner** : placez le document bien à plat, lancez le scan depuis le logiciel. Sauvegardez en PDF ou JPG.",
      "**Avec votre téléphone** : placez le document sur une surface plane et bien éclairée. Cadrez tout le document, sans couper les bords.",
      "**Application recommandée** : Adobe Scan (gratuite) ou CamScanner — elles corrigent automatiquement la perspective et améliorent la lisibilité.",
      "**Qualité minimale** : toutes les informations doivent être lisibles, pas de flou, pas d'ombre. Relisez la photo avant d'envoyer.",
      "**Format** : préférez le PDF pour les documents multi-pages, le JPG pour un document seul. Évitez le HEIC (iPhone) — convertissez d'abord.",
    ],
    question: "Vous photographiez votre carte d'identité mais elle est légèrement floue. Que faire ?",
    options: [
      { id: 'a', text: "Reprendre la photo jusqu'à ce qu'elle soit parfaitement nette", correct: true },
      { id: 'b', text: "L'envoyer quand même, l'organisme comprendra", correct: false },
      { id: 'c', text: "Envoyer la photo en précisant qu'elle est floue", correct: false },
    ],
    explication_reponse: "Un document illisible sera rejeté automatiquement — vous perdrez du temps et devrez tout recommencer. Prenez le temps d'avoir une photo parfaite : bonne lumière, document bien à plat, téléphone stable.",
  },
  {
    id: 'dossier-3',
    numero: 3,
    titre: "Nommer et organiser ses fichiers",
    sousTitre: "S'y retrouver facilement",
    contexte: "Vous avez 10 fichiers nommés 'Photo001', 'Scan002'... Impossible de s'y retrouver. Comment organiser ?",
    explication: [
      "**Un bon nom de fichier** contient : le type de document, votre nom et la date. Exemple : 'JustifDomicile_Dupont_Mars2026.pdf'",
      "**Créez des dossiers** sur votre ordinateur ou téléphone : un dossier 'Documents officiels' avec des sous-dossiers CAF, CPAM, Impôts, Banque.",
      "**Évitez les espaces** dans les noms de fichiers — utilisez des tirets ou des underscores. 'Carte identite.pdf' peut causer des problèmes, 'Carte_identite.pdf' non.",
      "**Sauvegarde** : copiez vos documents importants dans un cloud (Google Drive, OneDrive) — si vous perdez votre téléphone, vous ne perdez pas vos documents.",
      "**Un dossier 'À envoyer'** temporaire est très pratique : mettez-y les pièces rassemblées pour un dossier en cours, videz-le après envoi.",
    ],
    question: "Quel nom de fichier est le plus approprié pour votre RIB ?",
    options: [
      { id: 'a', text: "RIB_Dupont_2026.pdf", correct: true },
      { id: 'b', text: "photo003.jpg", correct: false },
      { id: 'c', text: "mon rib.pdf", correct: false },
    ],
    explication_reponse: "RIB_Dupont_2026.pdf est clair, sans espace, avec le type de document et l'année. Dans 6 mois vous saurez immédiatement ce que c'est. 'photo003.jpg' et 'mon rib.pdf' (avec espace) sont à éviter.",
  },
  {
    id: 'dossier-4',
    numero: 4,
    titre: "Envoyer et suivre son dossier",
    sousTitre: "Jusqu'à la confirmation",
    contexte: "Votre dossier est prêt. Comment l'envoyer et s'assurer qu'il a bien été reçu ?",
    explication: [
      "**Via l'espace personnel en ligne** : c'est la méthode la plus rapide et la plus sûre. CAF, CPAM, impôts — tous ont un espace en ligne avec accusé de réception automatique.",
      "**Par email** : joignez tous les documents en pièces jointes, mentionnez votre numéro de dossier dans l'objet. Demandez une confirmation de réception.",
      "**Par courrier recommandé** : pour les dossiers très importants ou les recours. Conservez le récépissé comme preuve d'envoi.",
      "**Notez la date d'envoi** et les documents envoyés dans un carnet ou sur votre téléphone.",
      "**Relancez si pas de réponse** sous 15 jours — un email courtois suffit : 'Je me permets de vérifier la bonne réception de mon dossier du [date]...'",
    ],
    question: "Vous envoyez votre dossier CAF en ligne. Comment avoir la preuve d'envoi ?",
    options: [
      { id: 'a', text: "Faire une capture d'écran de la confirmation affichée après envoi", correct: true },
      { id: 'b', text: "La CAF vous appellera pour confirmer", correct: false },
      { id: 'c', text: "Il n'y a pas de preuve d'envoi en ligne", correct: false },
    ],
    explication_reponse: "Après un envoi en ligne, un message de confirmation s'affiche — faites toujours une capture d'écran. C'est votre preuve en cas de litige. La CAF envoie aussi généralement un email de confirmation à conserver.",
  },
];
